import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import {
  NotificationData,
  NotificationService,
  LprAxisImagesReadyEvent,
} from '../services/notification.service';
import {
  getLicensePlateNumberFromNotification as getLicensePlateNumberFromNotificationUtil,
  getEventDisplayName as getEventDisplayNameUtil,
  openNotificationInModal,
} from '../shared/utils/notification-display.util';
import { ActiveSubscriptionGateService } from '../services/active-subscription-gate.service';
import {
  NotificationUtils,
  fetchLprEntries,
  upsertNotificationIntoList,
  NotificationListItem,
  markNotificationListItemAsRead,
  mergeKnownReadStateIntoNotificationList,
  resolveLprEntryIdFromListItem,
  resolveLprEntryIdFromNotificationData,
  resolveOutOfRangeListPage,
} from './notification.utils';
import { ModalStateService } from '../services/modal-state.service';
import { WayPurchaseModalStateService } from '../components/way-purchase-modal/services/way-purchase-modal-state.service';
import { MultiEligibleSelectionModalStateService } from '../components/multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import { ApiService } from '../services/api.service';
import { RoleService } from '../services/role.service';
import { UserRole } from '../shared/types/role.interface';
import { LocationService } from '../services/location.service';
import { EmployeeService } from '../services/employee.service';
import { runNotificationsInit, loadLocationsForNotificationsList } from './notifications-init.util';
import { TitleBarComponent } from '../shared/components/title-bar/title-bar.component';
import { SearchbarComponent } from '../shared/components/searchbar/searchbar.component';
import { NotificationTimestampPipe } from './notification-timestamp.pipe';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import {
  PaginationBarComponent,
  PaginationData,
} from '../shared/components/pagination-bar/pagination-bar.component';
import {
  SimpleDropdownComponent,
  DropdownOption,
} from '../shared/components/simple-dropdown/simple-dropdown.component';
import {
  ALL_LANES_FILTER_OPTION,
  loadLaneFilterOptions,
  shouldShowLaneFilter,
} from '../shared/utils/lane-filter.util';
import {
  FilterCategory,
  FilterModalComponent,
} from '../shared/components/filter-modal/filter-modal.component';
import { ActionButtonComponent } from '../shared/components/action-button/action-button.component';
import {
  buildNotificationEventFilterCategories,
  countActiveEventFilterOptions,
  notificationPassesEventFilter,
  readExcludedEventsFromCategories,
} from './notification-event-filter.util';
import {
  getNotificationsHiddenEventsPreference,
  resolvePreferenceUserId,
  setNotificationsHiddenEventsPreference,
} from '../shared/utils/user-preferences.util';

/** LPR entry fields this component reads off `NotificationData.data`. */
interface LprEntryFields {
  plate?: string;
  vehicle_license_plate?: string;
  license_plate?: string;
  state?: string;
  plate_image_s3_url?: string;
  image_s3_url?: string;
  vehicle_make?: string;
  vehicleMake?: string;
  make?: string;
  vehicle_model?: string;
  vehicleModel?: string;
  model?: string;
  lane_name?: string;
  laneName?: string;
  lane_id?: string;
}

/** Prepared display data for a single notifications-table row. */
interface NotificationRow {
  notification: NotificationListItem;
  timestamp: string;
  displayEvent: string;
  laneName: string;
  state: string;
  plate: string;
  plateImageUrl: string | null;
  vehicleImageUrl: string | null;
  make: string;
  model: string;
}

/**
 * Displays and manages a paginated list of notifications for the current user's location, with optional location filtering for admin users; supports real-time refreshes and opening notifications in a modal.
 * @component
 * @selector app-notifications-list
 * @example
 *   <app-notifications-list></app-notifications-list>
 * @input {none} none - No @Input properties. The component determines context (admin/staff) and loads data via injected services.
 * @output {none} none - No @Output events. Component communicates via injected NotificationService and opens modals via ModalStateService.
 *
 * Purpose:
 *   - Load and render notifications with server-side pagination.
 *   - Provide location-based filtering for admin users.
 *   - React to real-time push notifications and explicit refresh events to keep the list up to date.
 *
 * Key injected services:
 *   - ModalStateService: opens notification details in a modal.
 *   - ApiService: performs API calls to fetch notifications.
 *   - RoleService: determines whether current user is admin.
 *   - LocationService: loads available locations for admin users.
 *   - EmployeeService: retrieves current employee profile for staff users.
 *   - NotificationService: exposes notification$ and refresh$ streams for real-time and manual refreshes.
 *
 * Important properties:
 *   - notifications: Array<{ event: string; data: NotificationData; timestamp: string }> - current page of notifications displayed.
 *   - isLoading: boolean - flag preventing concurrent loads.
 *   - currentPage: number - current paginator page (UI uses 0-based; API expects 1-based conversion where applicable).
 *   - rowsPerPage: number - current number of rows per page.
 *   - totalRecords: number - total number of records returned from API (for pagination).
 *   - rowsPerPageOptions: number[] - available page size options.
 *   - availableLocations: Array<{id: string; name: string}> - location filter options for admins.
 *   - selectedLocationId: string | null - currently selected location id used for API calls.
 *   - isAdmin: boolean - whether the current user is an admin (drives location selection).
 *   - notificationSubscription, refreshSubscription: subscriptions to notification and refresh streams (cleaned up on destroy).
 *
 * Key methods:
 *   - ngOnInit(): Initializes component state, determines role, loads locations/profile via runNotificationsInit, subscribes to push/refresh events, and triggers initial loadNotifications().
 *   - ngOnDestroy(): Unsubscribes from notification and refresh subscriptions to avoid memory leaks.
 *   - loadNotifications(): Loads notifications from API with the current location and pagination; sets isLoading, updates notifications and totalRecords, and handles API errors.
 *   - getLocationIdToUse(): Returns the effective location id for API calls (selected location for admins, employee profile location for staff).
 *   - loadLocations(): Loads available locations for admin users and populates availableLocations.
 *   - onPageChange(page): Handles pagination-bar page changes (1-based page, stored 0-based in currentPage) and reloads notifications.
 *   - onRowsPerPageChange(rows): Handles rows-per-page change, resets to first page, and reloads notifications.
 *   - onLocationChange(): Handles admin location selection changes, resets pagination, and reloads notifications.
 *   - subscribeToPusherNotifications(): Subscribes to real-time notification stream and triggers reload when a new notification arrives.
 *   - subscribeToRefreshEvents(): Subscribes to manual refresh events and triggers reload.
 *   - getMessageForEventType(eventType): Maps a notification type to a friendly message via NotificationUtils.
 *   - getLicensePlateFromNotification(data): Extracts a license plate string from notification data for display.
 *   - getEventDisplayName(event): Returns a formatted event display name.
 *   - getFormattedTimestamp(timestamp): Formats a notification timestamp for display (human-friendly date/time).
 *   - onNotificationClick(notification): Opens the selected notification in a modal via ModalStateService.
 *   - paginationData: PaginationData model (1-based page) consumed by the shared app-pagination-bar.
 *
 * Lifecycle hooks:
 *   - ngOnInit: Determines admin/staff flow, initializes locations/profile, subscribes to notification and refresh streams, and triggers the initial data load.
 *   - ngOnDestroy: Cleans up subscriptions to prevent memory leaks.
 *
 * Notes:
 *   - Pagination: Component manages UI 0-based paging and converts as needed for APIs that are 1-based.
 *   - Admin behavior: If user is admin, component loads available locations and allows selecting one to filter notifications; if none selected, it defaults to the first available location.
 *   - Helpers used: fetchLprEntries, loadLocationsForNotificationsList, runNotificationsInit, NotificationUtils, getLicensePlateFromNotificationUtil, getEventDisplayNameUtil, getFormattedNotificationTimestamp, openNotificationInModal - these encapsulate API/formatting/modal logic.
 */
@Component({
  selector: 'app-notifications-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TitleBarComponent,
    SearchbarComponent,
    NotificationTimestampPipe,
    FormsModule,
    MatSelectModule,
    SimpleDropdownComponent,
    PaginationBarComponent,
    FilterModalComponent,
    ActionButtonComponent,
  ],
  templateUrl: './notifications-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './notifications-list.component.scss',
})
export class NotificationsListComponent implements OnInit, OnDestroy {
  private _notifications: NotificationListItem[] = [];

  /**
   * Prepared per-row view model. Rebuilt only when `notifications` changes so the
   * template binds to plain fields instead of calling helpers on every CD cycle.
   */
  notificationRows: NotificationRow[] = [];

  /** Backing list of raw notifications; assigning rebuilds {@link notificationRows}. */
  get notifications(): NotificationListItem[] {
    return this._notifications;
  }
  set notifications(value: NotificationListItem[]) {
    this._notifications = value ?? [];
    this.notificationRows = this._notifications.map(n => this.buildNotificationRow(n));
  }

  /** True while a list fetch is in flight. */
  isLoading = true;
  /** False until the first fetch finishes — prevents "No notifications" flash before API responds. */
  hasInitialLoadCompleted = false;
  /** Drives list fetches through switchMap so the latest term/page always wins. */
  private notificationsLoad$ = new Subject<void>();
  private loadSubscription?: Subscription;

  // Pagination properties (API uses 1-based page numbers)
  currentPage = 0; // 0-based here; the pagination bar and the API are 1-based
  rowsPerPage = 50;
  totalRecords = 0;

  rowsPerPageOptions = [10, 25, 50, 100];

  /** Pagination model for the shared pagination bar (1-based page). */
  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage + 1,
      totalPages: Math.max(1, Math.ceil(this.totalRecords / this.rowsPerPage)),
      totalItems: this.totalRecords,
      itemsPerPage: this.rowsPerPage,
    };
  }

  // Location filter (for admin only)
  availableLocations: { id: string; name: string }[] = [];
  selectedLocationId: string | null = null;
  isAdmin = false;

  // Lane filter (backend `lane_id`; empty = all lanes) — independent of header Current Lane
  laneFilterOptions: DropdownOption[] = [ALL_LANES_FILTER_OPTION];
  selectedLaneFilterId = '';
  isLaneDropdownOpen = false;

  // Hide the lane filter when there is at most one selectable lane.
  get showLaneFilter(): boolean {
    return shouldShowLaneFilter(this.laneFilterOptions);
  }

  // Search by license plate (server-side: term is sent to the API and the list is reloaded)
  searchTerm = '';

  // CWS-2051: Event filter — raw event slugs to hide (server-side `exclude_events`).
  excludedEvents: string[] = [];
  isEventFilterModalVisible = false;
  eventFilterCategories: FilterCategory[] = [];

  /** Number of checked events, shown as the Filter button badge. */
  get activeEventFilterCount(): number {
    return countActiveEventFilterOptions(this.excludedEvents);
  }

  // License plate / vehicle image overlay
  isImageOverlayOpen = false;
  overlayImageUrl: string | null = null;
  overlayPlate = '';

  private notificationSubscription: any;
  private lprEntryUpdatedSubscription: any;
  private lprImagesReadySubscription?: Subscription;
  Math = Math;

  constructor(
    private modalStateService: ModalStateService,
    private wayPurchaseModalState: WayPurchaseModalStateService,
    private multiEligibleModalState: MultiEligibleSelectionModalStateService,
    private apiService: ApiService,
    private roleService: RoleService,
    private locationService: LocationService,
    private employeeService: EmployeeService,
    private notificationService: NotificationService,
    private messageService: NotificationService,
    private activeSubscriptionGateService: ActiveSubscriptionGateService
  ) {}

  /**
   * Initialize the notifications list component: determine admin status, prepare and run notifications initialization flow (including loading locations and employee profile), set default selected location when available, load notifications, and subscribe to pusher and refresh events.
   * @example
   * gOnInit()
   * void
   * @returns {void} Initializes state and side-effects for the notifications list component.
   **/
  ngOnInit(): void {
    this.isLoading = true;
    this.hasInitialLoadCompleted = false;

    // Check if user is admin
    const userRole = this.roleService.getCurrentUserRole();
    this.isAdmin = userRole === UserRole.ADMIN;

    this.excludedEvents = this.readSavedExcludedEvents();

    // Set up the fetch stream before any load is triggered below.
    this.setupNotificationsLoadStream();

    // Load locations for admin, then load notifications
    runNotificationsInit({
      isAdmin: this.isAdmin,
      loadLocations: () => this.loadLocations(),
      getCurrentEmployeeProfile: () => this.employeeService.getCurrentEmployeeProfile(),
      onAdminSuccess: () => {
        if (this.availableLocations.length > 0 && !this.selectedLocationId) {
          this.selectedLocationId = this.availableLocations[0].id;
        }
        this.reloadLaneFilterOptions();
        this.loadNotifications();
      },
      onStaffSuccess: (response: any) => {
        if (response.status && response.data?.location_id) {
          this.selectedLocationId = response.data.location_id;
        }
        this.reloadLaneFilterOptions();
        this.loadNotifications();
      },
      onError: () => {
        this.reloadLaneFilterOptions();
        this.loadNotifications();
      },
    });

    this.subscribeToPusherNotifications();
    this.subscribeToLprEntryUpdates();
    this.subscribeToLprAxisImagesReady();
  }

  ngOnDestroy(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    if (this.lprEntryUpdatedSubscription) {
      this.lprEntryUpdatedSubscription.unsubscribe();
    }
    this.lprImagesReadySubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
    this.notificationsLoad$.complete();
  }

  /**
   * Request a notifications reload. The actual fetch runs through a switchMap
   * stream, so a newer request always cancels any in-flight one and the latest
   * search term/page drives the rendered results (no stale responses).
   */
  loadNotifications(): void {
    this.isLoading = true;
    this.notificationsLoad$.next();
  }

  /**
   * Wire the load stream once: each trigger cancels the previous in-flight
   * request via switchMap and only the most recent response is applied.
   */
  private setupNotificationsLoadStream(): void {
    this.loadSubscription = this.notificationsLoad$
      .pipe(
        switchMap(() => {
          const locationId = this.getLocationIdToUse();

          if (!locationId) {
            console.warn('No location ID available');
            return of({ newNotifications: [] as NotificationListItem[], paginationData: null });
          }

          return fetchLprEntries(this.apiService, locationId, {
            page: this.currentPage,
            limit: this.rowsPerPage,
            search: this.searchTerm,
            laneId: this.selectedLaneFilterId || null,
            excludeEvents: this.excludedEvents,
          }).pipe(
            catchError(() =>
              of({ newNotifications: [] as NotificationListItem[], paginationData: null })
            )
          );
        })
      )
      .subscribe(({ newNotifications, paginationData }) => {
        this.notifications = mergeKnownReadStateIntoNotificationList(
          newNotifications,
          this._notifications
        );
        this.totalRecords = paginationData ? paginationData.total : newNotifications.length;

        // Total shrank below the current page (refresh / live removals): jump to the last page.
        const fallbackPage = resolveOutOfRangeListPage(
          this.currentPage,
          this.rowsPerPage,
          this.totalRecords,
          newNotifications.length
        );
        if (fallbackPage !== null) {
          this.currentPage = fallbackPage;
          this.loadNotifications();
          return;
        }

        this.finishNotificationsLoad();
      });
  }

  private finishNotificationsLoad(): void {
    this.isLoading = false;
    this.hasInitialLoadCompleted = true;
  }

  /**
   * Get location ID to use for API call
   */
  private getLocationIdToUse(): string | null {
    if (this.isAdmin) {
      // For admin: use selected location or first location
      return (
        this.selectedLocationId ||
        (this.availableLocations.length > 0 ? this.availableLocations[0].id : null)
      );
    } else {
      // For staff: use the location_id from employee profile (set in ngOnInit)
      return this.selectedLocationId;
    }
  }

  /**
   * Load locations for admin users
   */
  private loadLocations(): any {
    return loadLocationsForNotificationsList(this.locationService, locs => {
      this.availableLocations = locs;
    });
  }

  /**
   * Handle page change - reload from API with new page
   */
  onPageChange(page: number): void {
    this.currentPage = page - 1; // Pagination bar emits 1-based pages
    this.loadNotifications(); // Reload from API with new pagination
  }

  /**
   * Handle rows per page change - reload from API with new limit
   */
  onRowsPerPageChange(rows: number): void {
    this.rowsPerPage = rows;
    this.currentPage = 0; // Reset to first page
    this.loadNotifications(); // Reload from API with new pagination
  }

  /**
   * Handle location change (admin only) - reload from API
   */
  onLocationChange(): void {
    this.currentPage = 0; // Reset to first page when changing location
    this.selectedLaneFilterId = '';
    this.isLaneDropdownOpen = false;
    this.locationService.setShopLocationById(this.selectedLocationId ?? '');
    this.reloadLaneFilterOptions();
    this.loadNotifications(); // Reload from API with new location
  }

  toggleLaneDropdown(): void {
    this.isLaneDropdownOpen = !this.isLaneDropdownOpen;
  }

  closeLaneDropdown(): void {
    this.isLaneDropdownOpen = false;
  }

  selectLaneFilter(laneId: string): void {
    this.selectedLaneFilterId = laneId || '';
    this.isLaneDropdownOpen = false;
    this.currentPage = 0;
    this.loadNotifications();
  }

  private reloadLaneFilterOptions(): void {
    const requestedLocationId = this.getLocationIdToUse();
    loadLaneFilterOptions(this.apiService, requestedLocationId).subscribe({
      next: options => {
        // Ignore stale responses if the location changed while this request was in flight.
        if (requestedLocationId !== this.getLocationIdToUse()) {
          return;
        }
        this.laneFilterOptions = options;
        if (
          this.selectedLaneFilterId &&
          !options.some(option => option.value === this.selectedLaneFilterId)
        ) {
          this.selectedLaneFilterId = '';
        }
        // Recompute rows so SSE-inserted entries that only had lane_id can resolve names.
        this.notificationRows = this._notifications.map(n => this.buildNotificationRow(n));
      },
      error: () => {
        if (requestedLocationId !== this.getLocationIdToUse()) {
          return;
        }
        this.laneFilterOptions = [ALL_LANES_FILTER_OPTION];
        this.notificationRows = this._notifications.map(n => this.buildNotificationRow(n));
      },
    });
  }

  private getMessageForEventType(eventType: NotificationData['type']): string {
    return NotificationUtils.getMessageForEventType(eventType);
  }

  /** Pusher rows (new + `updated_pusher`) — merge in-place; no full LPR list reload. */
  private subscribeToPusherNotifications(): void {
    this.notificationSubscription = this.notificationService.notification$.subscribe({
      next: notification => {
        if (!notification) {
          return;
        }
        this.applyInPlaceLprEntryUpdate(notification);
      },
    });
  }

  /** Axis / PUT corrections — same in-place merge as Pusher updates. */
  private subscribeToLprEntryUpdates(): void {
    this.lprEntryUpdatedSubscription =
      this.notificationService.lprEntryUpdatedNotification$.subscribe({
        next: ({ notification }) => {
          this.applyInPlaceLprEntryUpdate(notification);
        },
      });
  }

  /** Patch the matching row only — no loader, no full API reload. */
  private applyInPlaceLprEntryUpdate(notification: NotificationData): void {
    // Only reflect live rows for the location currently being viewed. If the row is
    // already visible but now belongs to another location, remove it.
    if (!this.notificationMatchesSelectedLocation(notification)) {
      this.removeNotificationFromListIfPresent(notification);
      return;
    }
    // Respect the page lane filter the same way the list API does.
    if (!this.notificationMatchesSelectedLane(notification)) {
      this.removeNotificationFromListIfPresent(notification);
      return;
    }
    // Respect the page Event filter the same way the list API does (CWS-2051).
    if (!notificationPassesEventFilter(notification, this.excludedEvents)) {
      this.removeNotificationFromListIfPresent(notification);
      return;
    }
    // While a search is active and the update no longer matches the current term,
    // don't add it — but if the changed row is already visible it has moved out of
    // the filter, so remove it to stay consistent with the server-filtered results.
    if (this.hasActiveSearch && !this.notificationMatchesSearch(notification)) {
      this.removeNotificationFromListIfPresent(notification);
      return;
    }
    const prevLen = this.notifications.length;
    this.notifications = upsertNotificationIntoList(this.notifications, notification);
    if (this.notifications.length > prevLen && this.totalRecords > 0) {
      this.totalRecords += 1;
    }
  }

  /** Resolve the location id carried by a live notification (top-level or nested). */
  private getNotificationLocationId(notification: NotificationData): string {
    const d = (notification?.data ?? {}) as Record<string, any>;
    const nested = (d['data'] ?? {}) as Record<string, any>;
    return String(
      d['location_id'] ?? d['locationId'] ?? nested['location_id'] ?? nested['locationId'] ?? ''
    ).trim();
  }

  /**
   * True when a live notification belongs to the location currently being viewed.
   * When no location is selected, all rows are allowed. When a location IS selected,
   * the incoming location must explicitly match — a missing/blank payload location is
   * treated as non-matching so cross-location realtime updates never leak into the view.
   */
  private notificationMatchesSelectedLocation(notification: NotificationData): boolean {
    const selected = String(this.getLocationIdToUse() ?? '').trim();
    if (!selected) {
      return true;
    }
    return this.getNotificationLocationId(notification) === selected;
  }

  /** Resolve lane id from a live notification payload. */
  private getNotificationLaneId(notification: NotificationData): string {
    const d = (notification?.data ?? {}) as Record<string, any>;
    const nested = (d['data'] ?? {}) as Record<string, any>;
    return String(
      d['lane_id'] ?? d['laneId'] ?? nested['lane_id'] ?? nested['laneId'] ?? ''
    ).trim();
  }

  /** True when the live row matches the page lane filter (empty filter = all lanes). */
  private notificationMatchesSelectedLane(notification: NotificationData): boolean {
    const selected = String(this.selectedLaneFilterId ?? '').trim();
    if (!selected) {
      return true;
    }
    return this.getNotificationLaneId(notification) === selected;
  }

  /** Live Axis image URLs arrive after the row — patch the matching row's images. */
  private subscribeToLprAxisImagesReady(): void {
    this.lprImagesReadySubscription = this.notificationService.lprAxisImagesReady$.subscribe({
      next: payload => this.applyLprAxisImagesReady(payload),
    });
  }

  /**
   * Merge the plate / vehicle image URLs from an `lpr-axis-images-ready` event into the
   * already-visible row so the image column updates without a full reload.
   */
  private applyLprAxisImagesReady(payload: LprAxisImagesReadyEvent): void {
    const selected = String(this.getLocationIdToUse() ?? '').trim();
    const incomingLoc = String(payload.location_id ?? '').trim();
    if (selected && incomingLoc && incomingLoc !== selected) {
      return;
    }

    const targetId = String(payload.lpr_entry_id ?? '').trim();
    const targetPlate = String(payload.plate ?? '')
      .trim()
      .toLowerCase();
    // Key presence (not value) signals intent: a present key with an empty value means
    // "clear this image", while an absent key means "leave it unchanged".
    const hasPlateKey = Object.prototype.hasOwnProperty.call(payload, 'plate_image_s3_url');
    const hasVehicleKey = Object.prototype.hasOwnProperty.call(payload, 'image_s3_url');
    const plateImage =
      typeof payload.plate_image_s3_url === 'string' ? payload.plate_image_s3_url.trim() : '';
    const vehicleImage =
      typeof payload.image_s3_url === 'string' ? payload.image_s3_url.trim() : '';
    if ((!targetId && !targetPlate) || (!hasPlateKey && !hasVehicleKey)) {
      return;
    }

    const matchesRow = (item: NotificationListItem): boolean => {
      if (targetId) {
        return resolveLprEntryIdFromListItem(item) === targetId;
      }
      const plate = this.getLicensePlateFromNotification(item.data);
      return typeof plate === 'string' && plate.trim().toLowerCase() === targetPlate;
    };

    // Hot realtime path: find the single matching row and update only it (and its view
    // model) instead of rebuilding the whole notifications array on every event.
    const index = this._notifications.findIndex(matchesRow);
    if (index === -1) {
      return;
    }

    const item = this._notifications[index];
    const entry = { ...((item.data?.data ?? {}) as Record<string, any>) };
    if (hasPlateKey) {
      entry['plate_image_s3_url'] = plateImage || null;
    }
    if (hasVehicleKey) {
      entry['image_s3_url'] = vehicleImage || null;
    }
    const updatedItem = { ...item, data: { ...item.data, data: entry } };

    this._notifications[index] = updatedItem;
    this.notificationRows[index] = this.buildNotificationRow(updatedItem);
  }

  /** Drop a now-non-matching row from the visible list and keep totalRecords in sync. */
  private removeNotificationFromListIfPresent(notification: NotificationData): void {
    const targetId = resolveLprEntryIdFromNotificationData(
      notification as any as Record<string, any>
    );
    if (!targetId) {
      return;
    }
    const next = this.notifications.filter(
      item => resolveLprEntryIdFromListItem(item) !== targetId
    );
    if (next.length === this.notifications.length) {
      return; // not currently visible — nothing to remove
    }
    this.notifications = next;
    if (this.totalRecords > 0) {
      this.totalRecords -= 1;
    }
  }

  /**
   * Extract license plate from notification data
   */
  getLicensePlateFromNotification(data: NotificationData): string {
    return getLicensePlateNumberFromNotificationUtil(data);
  }

  /**
   * Get formatted event name for display
   */
  getEventDisplayName(event: string, data?: NotificationData): string {
    return getEventDisplayNameUtil(event, data);
  }

  /** True when a search is active. */
  get hasActiveSearch(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  /**
   * Handle searchbar changes (server-side license plate search).
   * Resets to the first page and reloads from the API.
   */
  onSearchChange(term: string): void {
    const next = (term ?? '').trim();
    if (next === this.searchTerm.trim()) {
      return;
    }
    this.searchTerm = next;
    this.currentPage = 0;
    this.loadNotifications();
  }

  openEventFilterModal(): void {
    this.eventFilterCategories = buildNotificationEventFilterCategories(this.excludedEvents);
    this.isEventFilterModalVisible = true;
  }

  closeEventFilterModal(): void {
    this.isEventFilterModalVisible = false;
  }

  /** Apply the modal's checked events as the new exclusion list and reload from page 1. */
  onEventFilterApplied(categories: FilterCategory[]): void {
    this.isEventFilterModalVisible = false;
    this.setExcludedEvents(readExcludedEventsFromCategories(categories));
  }

  private setExcludedEvents(events: string[]): void {
    const unchanged =
      events.length === this.excludedEvents.length &&
      events.every(event => this.excludedEvents.includes(event));
    if (unchanged) {
      return;
    }
    this.excludedEvents = events;
    const userId = resolvePreferenceUserId();
    if (userId) {
      setNotificationsHiddenEventsPreference(userId, events);
    }
    this.currentPage = 0;
    this.loadNotifications();
  }

  private readSavedExcludedEvents(): string[] {
    const userId = resolvePreferenceUserId();
    return userId ? getNotificationsHiddenEventsPreference(userId) : [];
  }

  /**
   * Extract the LPR entry payload that carries plate / image / make / model fields.
   */
  private getEntryData(data: NotificationData): LprEntryFields {
    return (data?.data as LprEntryFields) ?? {};
  }

  /**
   * True when the notification's plate matches the active search term
   * (partial, case-insensitive — mirrors the server-side ILIKE filter).
   * Checks both the displayed plate and the raw stored plate variants.
   */
  private notificationMatchesSearch(notification: NotificationData): boolean {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return true;
    }
    const entry = this.getEntryData(notification);
    const candidates = [
      this.getLicensePlateFromNotification(notification),
      entry['plate'],
      entry['vehicle_license_plate'],
      entry['license_plate'],
    ];
    return candidates.some(
      value => typeof value === 'string' && value.toLowerCase().includes(term)
    );
  }

  /**
   * Resolve an image URL by preferred key, falling back to the other capture
   * when the preferred one is unavailable. Single place for image selection rules.
   */
  private resolveImageUrl(
    data: NotificationData,
    preferredKey: keyof LprEntryFields,
    fallbackKey: keyof LprEntryFields
  ): string | null {
    const entry = this.getEntryData(data);
    return entry[preferredKey] || entry[fallbackKey] || null;
  }

  /**
   * License plate crop image (`plate_image_s3_url`) shown in the table image column.
   * Falls back to the full vehicle capture when the plate crop is unavailable.
   */
  getPlateImageUrl(data: NotificationData): string | null {
    return this.resolveImageUrl(data, 'plate_image_s3_url', 'image_s3_url');
  }

  /**
   * Full vehicle / lane capture (`image_s3_url`) shown in the overlay (vehicle view).
   * Falls back to the plate crop when the vehicle capture is unavailable.
   */
  getVehicleImageUrl(data: NotificationData): string | null {
    return this.resolveImageUrl(data, 'image_s3_url', 'plate_image_s3_url');
  }

  /** Trim a vehicle field, drop empty / "null" placeholder values, and uppercase for display. */
  private normalizeVehicleField(value: string | undefined): string {
    const trimmed = (value ?? '').trim();
    return trimmed && trimmed.toLowerCase() !== 'null' ? trimmed.toUpperCase() : '';
  }

  /** Vehicle make for the table column (DB stores it as `vehicle_make`; empty when not available). */
  getVehicleMake(data: NotificationData): string {
    const entry = this.getEntryData(data);
    return (
      this.normalizeVehicleField(entry.vehicle_make) ||
      this.normalizeVehicleField(entry.vehicleMake) ||
      this.normalizeVehicleField(entry.make)
    );
  }

  /** Vehicle model for the table column (DB stores it as `vehicle_model`; empty when not available). */
  getVehicleModel(data: NotificationData): string {
    const entry = this.getEntryData(data);
    return (
      this.normalizeVehicleField(entry.vehicle_model) ||
      this.normalizeVehicleField(entry.vehicleModel) ||
      this.normalizeVehicleField(entry.model)
    );
  }

  /** Plate state code (2-char shorthand, uppercased; empty when not available). */
  getPlateState(data: NotificationData): string {
    return this.normalizeVehicleField(this.getEntryData(data).state).toUpperCase();
  }

  /**
   * Lane display name for the table column (empty when not available).
   * Prefers `lane_name` from the payload; when SSE only carries `lane_id`
   * (common before page reload), resolve the label from the location's lane filter.
   */
  getLaneName(data: NotificationData): string {
    const entry = this.getEntryData(data);
    const top = data as any as Record<string, any>;
    const nested = (top['data'] ?? {}) as Record<string, any>;
    const raw = String(
      entry.lane_name ??
        entry.laneName ??
        top['lane_name'] ??
        top['laneName'] ??
        nested['lane_name'] ??
        nested['laneName'] ??
        ''
    ).trim();
    if (raw && raw.toLowerCase() !== 'null') {
      return raw;
    }

    const laneId = String(
      entry.lane_id ??
        top['lane_id'] ??
        top['laneId'] ??
        nested['lane_id'] ??
        nested['laneId'] ??
        ''
    ).trim();
    if (!laneId) {
      return '';
    }
    const match = this.laneFilterOptions.find(
      option => String(option.value ?? '').trim() === laneId
    );
    const label = String(match?.label ?? '').trim();
    return label && label.toLowerCase() !== 'all lanes' ? label : '';
  }

  /** Build the prepared display row for a notification (computed once per load). */
  private buildNotificationRow(notification: NotificationListItem): NotificationRow {
    return {
      notification,
      timestamp: notification.timestamp,
      displayEvent: this.getEventDisplayName(notification.event, notification.data),
      laneName: this.getLaneName(notification.data),
      state: this.getPlateState(notification.data),
      plate: this.getLicensePlateFromNotification(notification.data),
      plateImageUrl: this.getPlateImageUrl(notification.data),
      vehicleImageUrl: this.getVehicleImageUrl(notification.data),
      make: this.getVehicleMake(notification.data),
      model: this.getVehicleModel(notification.data),
    };
  }

  /**
   * Open the vehicle view overlay for a row's captured image.
   * Stops propagation so the row click (open notification modal) is not triggered.
   */
  openImageOverlay(row: NotificationRow, event: Event): void {
    event.stopPropagation();
    if (!row.vehicleImageUrl) {
      return;
    }
    this.overlayImageUrl = row.vehicleImageUrl;
    this.overlayPlate = row.plate;
    this.isImageOverlayOpen = true;
  }

  /** Close the vehicle view overlay. */
  closeImageOverlay(): void {
    this.isImageOverlayOpen = false;
    this.overlayImageUrl = null;
    this.overlayPlate = '';
  }

  /**
   * Handle notification item click - open notification modal
   */
  onNotificationClick(notification: NotificationListItem): void {
    if (this.roleService.isStaff()) {
      return;
    }
    markNotificationListItemAsRead(
      this.apiService,
      notification,
      () => this.notifications,
      list => {
        this.notifications = list;
      }
    );
    openNotificationInModal(
      notification,
      this.modalStateService,
      this.wayPurchaseModalState,
      this.messageService,
      this.activeSubscriptionGateService,
      this.multiEligibleModalState
    );
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
